import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Clock } from "lucide-react";
import { toast } from "sonner";

const BLOCK_TYPES = ["Announcements", "Discussion", "Movement Warm Up", "Brain Warm Up", "Game", "Wrap Up", "If Time", "Home Improv"];
const EMPTY_CLASS = { level: 1, session_no: 1, class_no: 1, title: "", blocks: [] };
const newBlock = () => ({ order: 0, duration_min: 5, type: "Game", title: "", game_id: "", content: "" });

export function AdminCurriculum() {
  const [classes, setClasses] = useState([]);
  const [games, setGames] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/classes").then(({ data }) => setClasses(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/games").then(({ data }) => setGames(data)).catch(() => {});
  }, []);

  const openNew = () => { setEditing({ ...EMPTY_CLASS, id: null, blocks: [newBlock()] }); setOpen(true); };
  const openEdit = async (c) => {
    try {
      const { data } = await api.get(`/admin/classes/${c.id}`);
      setEditing({ ...data, blocks: (data.blocks || []).map((b) => ({ ...b, game_id: b.game_id || "" })) });
      setOpen(true);
    } catch { toast.error("Could not load class"); }
  };

  const setField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const setBlock = (i, patch) => setEditing((e) => ({ ...e, blocks: e.blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));
  const addBlock = () => setEditing((e) => ({ ...e, blocks: [...e.blocks, newBlock()] }));
  const removeBlock = (i) => setEditing((e) => ({ ...e, blocks: e.blocks.filter((_, j) => j !== i) }));
  const moveBlock = (i, dir) => setEditing((e) => {
    const b = [...e.blocks];
    const j = i + dir;
    if (j < 0 || j >= b.length) return e;
    [b[i], b[j]] = [b[j], b[i]];
    return { ...e, blocks: b };
  });

  const save = async () => {
    const payload = {
      level: parseInt(editing.level, 10) || 1,
      session_no: parseInt(editing.session_no, 10) || 1,
      class_no: parseInt(editing.class_no, 10) || 1,
      title: editing.title.trim(),
      blocks: editing.blocks.map((b, i) => ({
        order: i + 1, duration_min: parseInt(b.duration_min, 10) || 0, type: b.type,
        title: b.title || "", game_id: b.game_id || null, content: b.content || "",
      })),
    };
    if (!payload.title) { toast.error("Class title is required"); return; }
    try {
      if (editing.id) await api.put(`/admin/classes/${editing.id}`, payload);
      else await api.post("/admin/classes", payload);
      toast.success(editing.id ? "Class updated" : "Class created");
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete class "${c.title}" (L${c.level} S${c.session_no} C${c.class_no})?`)) return;
    try {
      await api.delete(`/admin/classes/${c.id}`);
      setClasses((cs) => cs.filter((x) => x.id !== c.id));
      toast.success("Class deleted");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-curriculum">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[#545454]">{classes.length} classes. Build the interactive agenda directors run live, download, or email.</p>
        <Button onClick={openNew} className="bg-[#920e12] hover:bg-[#550001] text-white" data-testid="add-class-btn"><Plus size={16} className="mr-1" /> Add class</Button>
      </div>
      <div className="overflow-x-auto bg-white rounded-2xl border border-[#dfd8da]">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#f9f6f6] text-[#545454] uppercase text-xs tracking-wide">
            <tr><th className="p-3">Level</th><th className="p-3">Session</th><th className="p-3">Class</th><th className="p-3">Title</th><th className="p-3">Blocks</th><th className="p-3">Actions</th></tr>
          </thead>
          <tbody>
            {classes.map((c) => (
              <tr key={c.id} className="border-t border-[#dfd8da]" data-testid={`admin-class-${c.id}`}>
                <td className="p-3 font-bold text-[#1a1617]">{c.level}</td>
                <td className="p-3">{c.session_no}</td>
                <td className="p-3">{c.class_no}</td>
                <td className="p-3">{c.title}</td>
                <td className="p-3 text-[#545454]">{c.block_count}</td>
                <td className="p-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-class-${c.id}`} className="border-[#920e12] text-[#920e12]"><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(c)} data-testid={`delete-class-${c.id}`} className="border-[#991b1b] text-[#991b1b]"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {classes.length === 0 && <p className="p-6 text-[#545454]">No classes yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="class-editor-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit class" : "New class"}</DialogTitle>
            <DialogDescription>Set the class placement and build its timed agenda blocks in the order they will run.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><Label htmlFor="c-level">Level</Label><input id="c-level" type="number" value={editing.level} onChange={(e) => setField("level", e.target.value)} data-testid="class-level" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" /></div>
                <div><Label htmlFor="c-sess">Session #</Label><input id="c-sess" type="number" value={editing.session_no} onChange={(e) => setField("session_no", e.target.value)} data-testid="class-session" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" /></div>
                <div><Label htmlFor="c-cls">Class #</Label><input id="c-cls" type="number" value={editing.class_no} onChange={(e) => setField("class_no", e.target.value)} data-testid="class-classno" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" /></div>
                <div className="col-span-2 sm:col-span-1"><Label htmlFor="c-title">Title</Label><input id="c-title" value={editing.title} onChange={(e) => setField("title", e.target.value)} data-testid="class-title" className="mt-1 w-full h-10 px-3 rounded-lg border border-[#d6cecf]" /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm uppercase tracking-widest font-extrabold text-[#920e12]">Timed blocks</h3>
                  <Button size="sm" variant="outline" onClick={addBlock} data-testid="add-block-btn" className="border-[#920e12] text-[#920e12]"><Plus size={14} className="mr-1" /> Add block</Button>
                </div>
                <div className="space-y-3">
                  {editing.blocks.map((b, i) => (
                    <div key={i} className="rounded-xl border border-[#dfd8da] p-3 bg-[#f9f6f6]" data-testid={`block-${i}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-6 h-6 rounded-full bg-[#920e12] text-white text-xs flex items-center justify-center font-bold">{i + 1}</span>
                        <select value={b.type} onChange={(e) => setBlock(i, { type: e.target.value })} data-testid={`block-type-${i}`} className="h-9 px-2 rounded-md border border-[#d6cecf] bg-white text-sm">
                          {BLOCK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <div className="flex items-center gap-1 text-sm text-[#545454]"><Clock size={14} />
                          <input type="number" value={b.duration_min} onChange={(e) => setBlock(i, { duration_min: e.target.value })} data-testid={`block-duration-${i}`} className="w-16 h-9 px-2 rounded-md border border-[#d6cecf]" /> min
                        </div>
                        <div className="ml-auto flex gap-1">
                          <button onClick={() => moveBlock(i, -1)} data-testid={`block-up-${i}`} className="p-1.5 rounded-md border border-[#d6cecf] hover:bg-white" aria-label="Move up"><ChevronUp size={14} /></button>
                          <button onClick={() => moveBlock(i, 1)} data-testid={`block-down-${i}`} className="p-1.5 rounded-md border border-[#d6cecf] hover:bg-white" aria-label="Move down"><ChevronDown size={14} /></button>
                          <button onClick={() => removeBlock(i)} data-testid={`block-remove-${i}`} className="p-1.5 rounded-md border border-[#991b1b] text-[#991b1b] hover:bg-[#fee2e2]" aria-label="Remove block"><Trash2 size={14} /></button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input value={b.title} onChange={(e) => setBlock(i, { title: e.target.value })} data-testid={`block-title-${i}`} placeholder="Block title" className="h-9 px-2 rounded-md border border-[#d6cecf] text-sm" />
                        <select value={b.game_id} onChange={(e) => setBlock(i, { game_id: e.target.value })} data-testid={`block-game-${i}`} className="h-9 px-2 rounded-md border border-[#d6cecf] bg-white text-sm">
                          <option value="">— No game —</option>
                          {games.map((g) => <option key={g.id} value={g.id}>{g.name} (L{g.level})</option>)}
                        </select>
                      </div>
                      <textarea value={b.content} onChange={(e) => setBlock(i, { content: e.target.value })} data-testid={`block-content-${i}`} rows={2} placeholder="Notes / content (for non-game blocks)" className="mt-2 w-full px-2 py-1.5 rounded-md border border-[#d6cecf] text-sm" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#920e12] text-white" data-testid="save-class-btn">Save class</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
